/* eslint-disable react/no-danger -- preview HTML is rendered by the API's email renderer. */
import * as React from 'react'
import {
  Bold,
  Braces,
  ChevronLeft,
  Image,
  Italic,
  Link2,
  List,
  Minus,
  MousePointerClick,
  Type,
} from 'lucide-react'
import { useMutation } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { ApiError, postJSON } from '@/lib/api'

type Chapter = { id: number; name: string; archived: boolean }
type Preview = { preview: { subject: string; body: string } }

const toolbar = [
  ['Heading', Type],
  ['Bold', Bold],
  ['Italic', Italic],
  ['Link', Link2],
  ['List', List],
  ['Image', Image],
  ['Button', MousePointerClick],
  ['Divider', Minus],
  ['Merge field', Braces],
] as const

export function MemberPortalLeaderCompose({
  chapter,
  associationName,
  blastPaused = false,
}: {
  chapter: Chapter
  associationName: string
  blastPaused?: boolean
}) {
  const [subject, setSubject] = React.useState('')
  const [body, setBody] = React.useState('')
  const [preview, setPreview] = React.useState<Preview['preview'] | null>(null)
  const [confirmOpen, setConfirmOpen] = React.useState(false)
  const emailPath = `/api/v1/member_portal/leader/chapters/${chapter.id}/email`
  const request = { email: { subject, body } }
  const previewMutation = useMutation({
    mutationFn: () => postJSON<Preview>(`${emailPath}/preview`, request),
    onSuccess: (data) => setPreview(data.preview),
  })
  const testSend = useMutation({
    mutationFn: () => postJSON(`${emailPath}/test_send`, request),
    onSuccess: () => setConfirmOpen(false),
  })
  const send = useMutation({
    mutationFn: () =>
      postJSON(`${emailPath}`, {
        email: { ...request.email, idempotency_key: crypto.randomUUID() },
      }),
    onSuccess: () => setConfirmOpen(false),
  })
  const error = previewMutation.error ?? testSend.error ?? send.error
  const hasContent = subject.trim() !== '' && body.trim() !== ''
  const canSend = !blastPaused && hasContent

  return (
    <div className="mt-6 space-y-4">
      <Link
        to={`/portal/leader/chapters/${chapter.id}`}
        className="inline-flex items-center gap-1 text-body-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="size-4" aria-hidden="true" />
        {chapter.name}
      </Link>
      {blastPaused && (
        <Alert variant="warning">
          Sending is paused for the whole association. Contact the office.
        </Alert>
      )}
      <ComposeHeader
        chapter={chapter}
        hasContent={hasContent}
        canSend={canSend}
        previewPending={previewMutation.isPending}
        testSendPending={testSend.isPending}
        onPreview={() => previewMutation.mutate()}
        onTestSend={() => testSend.mutate()}
        onSend={() => setConfirmOpen(true)}
      />
      {error && (
        <Alert>{error instanceof ApiError ? error.message : 'Unable to send this email.'}</Alert>
      )}
      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <ComposeEditor
          subject={subject}
          body={body}
          preview={preview}
          onSubjectChange={setSubject}
          onBodyChange={setBody}
        />
        <AudienceRail chapter={chapter} associationName={associationName} />
      </div>
      <ConfirmDialog
        open={confirmOpen}
        chapter={chapter}
        canSend={canSend}
        onClose={() => setConfirmOpen(false)}
        onSend={() => send.mutate()}
      />
    </div>
  )
}

function ComposeHeader({
  chapter,
  hasContent,
  canSend,
  previewPending,
  testSendPending,
  onPreview,
  onTestSend,
  onSend,
}: {
  chapter: Chapter
  hasContent: boolean
  canSend: boolean
  previewPending: boolean
  testSendPending: boolean
  onPreview: () => void
  onTestSend: () => void
  onSend: () => void
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <h1 className="text-subheading font-semibold">Email {chapter.name}</h1>
      <div className="flex shrink-0 gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={onPreview}
          disabled={!hasContent || previewPending}
        >
          Preview
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onTestSend}
          disabled={!hasContent || testSendPending}
        >
          Send test to me
        </Button>
        <Button type="button" onClick={onSend} disabled={!canSend}>
          Send
        </Button>
      </div>
    </div>
  )
}

function ComposeEditor({
  subject,
  body,
  preview,
  onSubjectChange,
  onBodyChange,
}: {
  subject: string
  body: string
  preview: Preview['preview'] | null
  onSubjectChange: (value: string) => void
  onBodyChange: (value: string) => void
}) {
  return (
    <Card className="overflow-hidden">
      <Toolbar />
      <CardContent className="space-y-4 p-4">
        <div className="max-w-2xl">
          <Label htmlFor="leader-email-subject">Subject</Label>
          <Input
            id="leader-email-subject"
            className="mt-1.5"
            value={subject}
            onChange={(event) => onSubjectChange(event.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="leader-email-body">Message</Label>
          <Textarea
            id="leader-email-body"
            className="mt-1.5 min-h-48"
            value={body}
            onChange={(event) => onBodyChange(event.target.value)}
          />
        </div>
        {preview && (
          <Card className="bg-muted/40">
            <CardContent className="space-y-3 p-4">
              <p className="text-body font-semibold">{preview.subject}</p>
              <div
                className="text-body-sm whitespace-pre-wrap"
                dangerouslySetInnerHTML={{ __html: preview.body }}
              />
            </CardContent>
          </Card>
        )}
      </CardContent>
    </Card>
  )
}

function Toolbar() {
  return (
    <div className="flex items-center gap-0.5 border-b border-border bg-background px-2 py-1.5">
      {toolbar.map(([label, Icon]) => (
        <Button
          key={label}
          type="button"
          variant="outline"
          size="sm"
          className="border-transparent px-2"
          aria-label={label}
        >
          <Icon className="size-4 text-muted-foreground" aria-hidden="true" />
        </Button>
      ))}
    </div>
  )
}

function AudienceRail({ chapter, associationName }: { chapter: Chapter; associationName: string }) {
  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <p className="text-caption text-muted-foreground">To</p>
        <p className="text-body-sm">{chapter.name}</p>
        <p className="text-body-sm text-muted-foreground">Recipients are resolved at send.</p>
        <p className="border-t border-border pt-3 text-body-sm">
          Staff can see this send in the Email Log.
        </p>
        <Badge variant="neutral">No scheduling</Badge>
        <p className="text-caption text-muted-foreground">
          {associationName} adds unsubscribe and postal details at send time.
        </p>
      </CardContent>
    </Card>
  )
}

function ConfirmDialog({
  open,
  chapter,
  canSend,
  onClose,
  onSend,
}: {
  open: boolean
  chapter: Chapter
  canSend: boolean
  onClose: () => void
  onSend: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send this chapter email?</DialogTitle>
        </DialogHeader>
        <p className="text-body-sm text-muted-foreground">
          The audience is fixed to {chapter.name}. Staff can see the send in the Email Log.
        </p>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Back
          </Button>
          <Button type="button" onClick={onSend} disabled={!canSend}>
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
